import type { LucideIcon } from 'lucide-react';
import { Bot, Building2, Coins, DollarSign } from 'lucide-react';
import { useT } from '@kmgestao/i18n';
import { PageStatTile, Skeleton } from '@kmgestao/ui';

import {
  formatCompactInteger,
  formatUsd,
  formatWholeNumber,
  type AiKpisSummary,
} from '../../../features/ai-analytics/index.js';

export interface KpiCardsProps {
  summary: AiKpisSummary | undefined;
  loading?: boolean;
}

export function KpiCards({ summary, loading = false }: KpiCardsProps) {
  const { t } = useT('dashboard');

  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label={t('page.title')}>
      <MetricCard
        title={t('cards.activeClinics.title')}
        description={t('cards.activeClinics.description')}
        value={loading || !summary ? null : formatWholeNumber(summary.activeClinics)}
        icon={Building2}
      />
      <MetricCard
        title={t('cards.calls.title')}
        description={
          summary
            ? t('cards.calls.breakdown', {
                platform: formatWholeNumber(summary.platformCalls),
                byok: formatWholeNumber(summary.byokCalls),
              })
            : t('cards.calls.description')
        }
        value={loading || !summary ? null : formatCompactInteger(summary.callsTotal)}
        icon={Bot}
      />
      <MetricCard
        title={t('cards.tokens.title')}
        description={
          summary
            ? t('cards.tokens.breakdown', {
                input: formatCompactInteger(summary.tokensInTotal),
                output: formatCompactInteger(summary.tokensOutTotal),
              })
            : t('cards.tokens.description')
        }
        value={loading || !summary ? null : formatCompactInteger(summary.tokensTotal)}
        icon={Coins}
      />
      <MetricCard
        title={t('cards.cost.title')}
        description={
          summary
            ? t('cards.cost.breakdown', {
                errors: formatWholeNumber(summary.errorCalls),
              })
            : t('cards.cost.description')
        }
        value={loading || !summary ? null : formatUsd(summary.costEstimatedUsdTotal)}
        icon={DollarSign}
      />
    </section>
  );
}

interface MetricCardProps {
  title: string;
  description: string;
  value: string | null;
  icon: LucideIcon;
}

function MetricCard({ title, description, value, icon: Icon }: MetricCardProps) {
  return (
    <PageStatTile
      label={title}
      value={value ? value : <Skeleton className="h-10 w-32" />}
      hint={description}
      icon={Icon}
    />
  );
}
